import { useRef, useState } from "react";
import { Loader2, Paperclip, X, Zap } from "lucide-react";

import ImageLightbox from "@/components/ImageLightbox";
import { NivaAvatar } from "@/components/NivaAvatar";
import { useToast } from "@/components/ui/toast";
import { PRIORITIES, STATUSES, fmtDateTime, uploadTicketImage } from "@/lib/supportTickets";
import { cn } from "@/lib/utils";
import type { SupportTicket, SupportTicketAssistantMessage, SupportTicketComment } from "@/types";

/** Pieces shared by the owner and admin support-ticket pages. */

export function StatusBadge({ value }: { value: string }) {
  const cfg = STATUSES.find((s) => s.value === value);
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap", cfg?.classes ?? "bg-slate-100 text-slate-500 border-slate-200")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", cfg?.dot ?? "bg-slate-400")} />
      {cfg?.label ?? value.replace("_", " ")}
    </span>
  );
}

export function PriorityBadge({ value }: { value: string }) {
  const cfg = PRIORITIES.find((p) => p.value === value);
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize", cfg?.color ?? "bg-slate-100 text-slate-600 border-slate-200")}>
      {value === "urgent" && <Zap className="mr-1 h-2.5 w-2.5" />}
      {value}
    </span>
  );
}

export type ThreadEntry =
  | (SupportTicketComment & { kind: "comment" })
  | (SupportTicketAssistantMessage & { kind: "niva" });

/**
 * The ticket's replies with Niva's messages (acknowledgment + 24h/48h
 * follow-ups) merged in by time. Niva goes first on a tie, so her
 * acknowledgment always sits right under the opening description.
 */
export function threadWithNiva(ticket: SupportTicket): ThreadEntry[] {
  const niva: ThreadEntry[] = (ticket.assistant_messages ?? []).map((m) => ({ ...m, kind: "niva" }));
  const comments: ThreadEntry[] = ticket.comments.map((c) => ({ ...c, kind: "comment" }));
  return [...niva, ...comments].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}

/** One of Niva's messages in a ticket conversation. */
export function NivaAckBubble({ text, at }: { text: string; at: string }) {
  return (
    <div className="flex items-end gap-2.5">
      <NivaAvatar className="h-8 w-8 mb-0.5" />
      <div className="max-w-[85%] space-y-1">
        <p className="text-[10px] font-medium text-muted-foreground pl-1">
          <span className="font-semibold text-primary">Niva</span> · AI Deal Assistant · {fmtDateTime(at)}
        </p>
        <div className="rounded-2xl rounded-bl-sm bg-primary/10 border border-primary/25 px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap shadow-sm">
          {text}
        </div>
      </div>
    </div>
  );
}

/** A reply's attached image: a thumbnail that opens full size. */
export function CommentImage({ url }: { url: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1.5 block overflow-hidden rounded-lg border border-border/60 bg-muted/20 hover:border-primary/40 transition-colors"
        aria-label="Open attached image"
      >
        <img src={url} alt="Attached image" className="max-h-40 max-w-[240px] object-contain" loading="lazy" />
      </button>
      {open && <ImageLightbox src={url} alt="Attached image" caption="Attached image" onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Paperclip button for a reply composer. Uploads on pick and reports the
 * stored URL; shows a removable chip once something is attached.
 */
export function ReplyAttachmentButton({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadTicketImage(file));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload image.");
    } finally {
      setUploading(false);
    }
  }

  if (value) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 py-0.5 pl-1 pr-1.5 text-[11px] font-medium text-primary">
        <img src={value} alt="" className="h-5 w-5 rounded-full object-cover" />
        Image attached
        <button type="button" onClick={() => onChange(null)} aria-label="Remove image" className="rounded-full p-0.5 hover:bg-primary/15">
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        title="Attach an image"
        aria-label="Attach an image"
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 transition-colors"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        {uploading ? "Uploading…" : "Attach image"}
      </button>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={handleFile} />
    </>
  );
}
